import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
import type { NetworkStatus } from 'apollo-client';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { LoadingState } from '../homeViewsQueryHelpers.ts';
import {
	getLoadingState,
	notEmpty,
	byTitle,
	notArchived,
	notDeleted,
	transformActivityData,
} from '../homeViewsQueryHelpers.ts';
import {
	transformRecentToPageCard,
	transformCreatedByMeToPageCard,
} from '../pageCardTransformers.ts';
import { shouldUseAGGActivityV3 } from '../shouldUseAGGActivityV3.ts';

import { getRecentQueryOptions } from './recentQueryHelpers.ts';
import type {
	HomeRecentActivitiesQuery as HomeRecentActivitiesQueryType,
	HomeRecentActivitiesQueryVariables,
} from './__types__/HomeRecentActivitiesQuery.ts';
import type {
	HomeRecentActivityQuery as HomeRecentActivityQueryType,
	HomeRecentActivityQueryVariables,
	HomeRecentActivityQuery_activity_myActivity_all as HomeRecentActivityAllType,
} from './__types__/HomeRecentActivityQuery.ts';
import type {
	HomeRecentCreatedQuery as HomeRecentCreatedQueryType,
	HomeRecentCreatedQueryVariables,
} from './__types__/HomeRecentCreatedQuery.ts';
type UseRecentData = ({
	pill,
	filterText,
	translatedUntitled,
}: {
	pill?: string;
	filterText: string;
	translatedUntitled: string;
}) => {
	pageCardData: GlobalPageCard[];
	loading: LoadingState;
	networkStatus: NetworkStatus;
	hasNextPage: boolean;
	loadMore: () => void;
	error?: ApolloErrorInterop;
};

export type QueryResult =
	| HomeRecentActivitiesQueryType
	| HomeRecentActivityQueryType
	| HomeRecentCreatedQueryType
	| undefined;
export type QueryVariables =
	| HomeRecentActivitiesQueryVariables
	| HomeRecentCreatedQueryVariables
	| HomeRecentActivityQueryVariables;

const isMyActivities = (
	data: QueryResult,
): data is HomeRecentActivitiesQueryType | HomeRecentActivityQueryType => {
	if (!data) {
		return false;
	}
	const activity = (data as HomeRecentActivityQueryType).activity;
	return activity !== undefined;
};

const isSearch = (data: QueryResult): data is HomeRecentCreatedQueryType => {
	if (!data) {
		return false;
	}
	return (data as HomeRecentCreatedQueryType).search !== undefined;
};

const normalizeData = (
	data: QueryResult,
	filterText: string,
	translatedUntitled: string,
	userId: string | null,
	isWhiteboardEmojiFGEnabled: boolean,
) => {
	let pageCardData: GlobalPageCard[] = [];
	let hasNextPage = false;
	let endCursor = '';

	const getObjectData = (node) => node.object?.data;

	if (isMyActivities(data)) {
		const myActivity = (data as HomeRecentActivityQueryType).activity?.myActivity;
		const all = myActivity?.all;
		pageCardData = (all?.edges || [])
			.map((edge) => edge?.node)
			.map((node) => transformActivityData(node, true))
			.filter(notEmpty)
			.filter((node) => notEmpty(getObjectData(node)))
			.filter((node) => notArchived(getObjectData(node)))
			.filter((node) => notDeleted(getObjectData(node)))
			.map((node) =>
				transformRecentToPageCard(node, translatedUntitled, isWhiteboardEmojiFGEnabled, true),
			)
			.filter((node) => byTitle(filterText, node.title.text));
		hasNextPage = all?.pageInfo.hasNextPage || false;
		endCursor = (all as HomeRecentActivityAllType)?.pageInfo?.endCursor || '';
	} else if (isSearch(data)) {
		pageCardData = (data.search?.nodes || [])
			.filter(notEmpty)
			.filter((node) => notEmpty(node.content))
			.filter((node) => notArchived(node.content))
			.filter((node) => notDeleted(node.content))
			.map((node) => transformCreatedByMeToPageCard(node, translatedUntitled, userId))
			.filter((node) => byTitle(filterText, node.title.text));
		hasNextPage = data.search?.pageInfo?.hasNextPage || false;
		endCursor = data.search?.pageInfo?.endCursor || '';
	}

	pageCardData = uniqBy(pageCardData, (pageCard) => pageCard.id);

	return {
		pageCardData,
		hasNextPage,
		endCursor,
	};
};

export const useRecentData: UseRecentData = ({ pill, filterText, translatedUntitled }) => {
	const { cloudId, userId } = useSessionData();
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardViewedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isWhiteboardUpdatedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabaseEnabled = isContentTypeEnabledInCurrentEnv('database');

	const { query, variables } = getRecentQueryOptions({
		pill,
		cloudId,
		endCursor: '',
		isWhiteboardViewedEnabled,
		isWhiteboardUpdatedEnabled,
		isDatabaseEnabled,
	});
	const { data, loading, error, networkStatus, fetchMore } = useQueryInterop<
		QueryResult,
		QueryVariables
	>(query, {
		variables,
		notifyOnNetworkStatusChange: true,
		...(shouldUseAGGActivityV3() && { interopApolloAggClient: true }),
	});
	const { pageCardData, hasNextPage, endCursor } = normalizeData(
		data,
		filterText,
		translatedUntitled,
		userId,
		fg('confluence_frontend_whiteboard_emoji_titles'),
	);

	const loadMore = () => {
		if (hasNextPage && endCursor && !loading) {
			void fetchMore({
				variables: getRecentQueryOptions({
					pill,
					cloudId,
					endCursor,
					isWhiteboardViewedEnabled,
					isWhiteboardUpdatedEnabled,
					isDatabaseEnabled,
				}).variables,
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					const result = { ...fetchMoreResult };
					if (isMyActivities(prev) && isMyActivities(fetchMoreResult)) {
						const myActivity = (prev as HomeRecentActivityQueryType)?.activity?.myActivity;
						const prevEdges = myActivity?.all?.edges || [];
						const fetchMoreEdges =
							(fetchMoreResult as HomeRecentActivityQueryType).activity?.myActivity?.all?.edges ||
							[];
						const mergedEdges = [...prevEdges, ...fetchMoreEdges];
						const myActivityEdgesString = 'activity.myActivity.all.edges';
						set(result, myActivityEdgesString, mergedEdges);
					} else if (isSearch(prev) && isSearch(fetchMoreResult)) {
						const prevNodes = prev.search?.nodes || [];
						const fetchMoreNodes = fetchMoreResult.search?.nodes || [];
						const mergedNodes = [...prevNodes, ...fetchMoreNodes];
						set(result, 'search.nodes', mergedNodes);
					}
					return result;
				},
			});
		}
	};

	return {
		pageCardData,
		loading: getLoadingState(networkStatus),
		networkStatus,
		error,
		hasNextPage,
		loadMore,
	};
};
